// src/FlagDropdown.js
import React, { useState, useEffect, useMemo } from 'react';
import Select, { components } from 'react-select';
import axios from 'axios';

const COMMON_COUNTRIES = [
  { value: 'us', dialCode: '+1', countryName: 'United States', flag: 'https://flagcdn.com/us.svg', label: '+1 United States US' },
  { value: 'gb', dialCode: '+44', countryName: 'United Kingdom', flag: 'https://flagcdn.com/gb.svg', label: '+44 United Kingdom GB' },
  { value: 'ca', dialCode: '+1', countryName: 'Canada', flag: 'https://flagcdn.com/ca.svg', label: '+1 Canada CA' },
  { value: 'au', dialCode: '+61', countryName: 'Australia', flag: 'https://flagcdn.com/au.svg', label: '+61 Australia AU' },
  { value: 'in', dialCode: '+91', countryName: 'India', flag: 'https://flagcdn.com/in.svg', label: '+91 India IN' },
  { value: 'ng', dialCode: '+234', countryName: 'Nigeria', flag: 'https://flagcdn.com/ng.svg', label: '+234 Nigeria NG' },
  { value: 'ke', dialCode: '+254', countryName: 'Kenya', flag: 'https://flagcdn.com/ke.svg', label: '+254 Kenya KE' },
  { value: 'de', dialCode: '+49', countryName: 'Germany', flag: 'https://flagcdn.com/de.svg', label: '+49 Germany DE' },
];

const FlagDropdown = ({ onSelect, defaultCountry = 'au' }) => {
  const [options, setOptions] = useState(COMMON_COUNTRIES);
  const [selectedValue, setSelectedValue] = useState(() => {
    return COMMON_COUNTRIES.find(c => c.value === defaultCountry) || COMMON_COUNTRIES[0];
  });

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const response = await axios.get('https://restcountries.com/v3.1/all?fields=cca2,flags,idd,name');

        if (response.data && Array.isArray(response.data)) {
          const flagOptions = response.data
            .filter(country => country.idd && country.idd.root)
            .map(country => {
              const root = country.idd.root || '';
              const suffixes = country.idd.suffixes || [];
              const dialCode = root + (suffixes.length === 1 ? suffixes[0] : '');
              
              return {
                value: country.cca2.toLowerCase(),
                dialCode: dialCode,
                countryName: country.name.common,
                flag: country.flags.svg || country.flags.png,
                label: `${dialCode} ${country.name.common} ${country.cca2}`
              };
            })
            .sort((a, b) => a.countryName.localeCompare(b.countryName));

          if (flagOptions.length > 0) {
            setOptions(flagOptions);
            const defaultOption = flagOptions.find(option => option.value === defaultCountry) || flagOptions[0];
            setSelectedValue(defaultOption);
            if (onSelect) onSelect(defaultOption.dialCode);
          }
        }
      } catch (error) {
        console.error('Error fetching flags:', error);
      }
    };

    fetchFlags();
  }, []);

  const handleChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedValue(selectedOption);
      if (onSelect) {
        onSelect(selectedOption.dialCode);
      }
    }
  };

  const formatOptionLabel = ({ flag, dialCode, countryName }, { context }) => {
    if (context === 'value') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img
            src={flag}
            alt=""
            style={{ width: '24px', height: '16px', borderRadius: '2px', objectFit: 'cover' }}
          />
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{dialCode}</span>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 0' }}>
        <img
          src={flag}
          alt=""
          style={{ width: '22px', height: '15px', borderRadius: '2px', objectFit: 'cover' }}
        />
        <span style={{ fontSize: '14px', color: '#333' }}>{countryName} ({dialCode})</span>
      </div>
    );
  };

  return (
    <Select
      options={options}
      onChange={handleChange}
      value={selectedValue}
      formatOptionLabel={formatOptionLabel}
      isSearchable={true}
      placeholder=""
      styles={{
        container: (provided) => ({
          ...provided,
          width: '120px', // Wider to fit flag + code
          zIndex: 1000
        }),
        control: (provided) => ({
          ...provided,
          border: 'none',
          boxShadow: 'none',
          backgroundColor: 'transparent',
          minHeight: '40px',
          cursor: 'pointer'
        }),
        menu: (provided) => ({
          ...provided,
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          border: '1px solid #eee',
          width: '300px',
          zIndex: 9999,
          marginTop: '5px'
        }),
        menuList: (provided) => ({
          ...provided,
          maxHeight: '250px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#eee', borderRadius: '10px' }
        }),
        option: (provided, state) => ({
          ...provided,
          backgroundColor: state.isSelected ? '#f0f7ff' : state.isFocused ? '#f8f9fa' : 'transparent',
          color: '#333',
          cursor: 'pointer',
          padding: '8px 12px'
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (provided) => ({
          ...provided,
          padding: '0 2px',
          color: '#333'
        }),
        input: (provided) => ({
          ...provided,
          color: 'transparent' // Hide typing in the trigger area
        })
      }}
    />
  );
};

export default FlagDropdown;